import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type {
  FiscalEnvironment,
  FiscalGap,
  FiscalRegime,
  FiscalSettingsResponse,
} from '@aeris/contracts'
import {
  Badge,
  Button,
  CheckboxField,
  ConfirmDialog,
  Field,
  InlineAlert,
  Input,
  Page,
  PageHeader,
  QueryState,
  Section,
  Select,
} from '@aeris/ui'

import { FiscalServiceItems } from '../components/fiscal-service-items.tsx'
import { FiscalTaxProfiles } from '../components/fiscal-tax-profiles.tsx'
import {
  ApiError,
  fetchFiscalSettings,
  removeFiscalCertificate,
  saveFiscalRegime,
  saveFiscalSettings,
  uploadFiscalCertificate,
} from '../lib/api.ts'

/**
 * A nota fiscal da empresa (F371, ADR-0051): o certificado A1, o regime, o
 * ambiente e o que ainda falta para emitir cada tipo de nota.
 *
 * O servidor nunca devolve o certificado: a tela só sabe de quem é e até quando
 * vale.
 */

const QUERY_KEY = ['fiscal-settings']

const message = (error: unknown, fallback: string): string =>
  error instanceof ApiError ? error.message : fallback

const GAP_LABELS: Readonly<Record<FiscalGap, string>> = {
  engine: 'O servidor ainda não tem o motor fiscal configurado.',
  'secret-key': 'O servidor ainda não tem a chave que protege o certificado.',
  certificate: 'Envie o certificado A1 da empresa.',
  'certificate-expired': 'O certificado A1 venceu. Envie o novo.',
  'certificate-other-company': 'O certificado é de outro CNPJ.',
  regime: 'Informe o regime tributário.',
  'regime-unsupported':
    'Por enquanto, o Aeris só emite para Simples Nacional dentro do sublimite.',
  'simples-percent': 'Informe o percentual do Simples que o contador indicar.',
  address: 'Complete o endereço em Empresa.',
  'city-code': 'Informe o código IBGE do município em Empresa.',
  'state-registration': 'Informe a Inscrição Estadual em Empresa.',
  'municipal-registration': 'Informe a Inscrição Municipal em Empresa.',
}

const REGIME_LABELS: Readonly<Record<FiscalRegime, string>> = {
  '1': 'Simples Nacional',
  '2': 'Simples Nacional, acima do sublimite',
  '3': 'Regime Normal',
}

const dateOf = (iso: string): string =>
  new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })

/** Lê o arquivo em base64, sem o prefixo `data:`. */
const readBase64 = async (file: File): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

export const NotaFiscalPage = (): ReactNode => {
  const settings = useQuery({
    queryKey: QUERY_KEY,
    queryFn: ({ signal }) => fetchFiscalSettings(signal),
  })

  return (
    <Page>
      <PageHeader
        title="Nota fiscal"
        description="O certificado, o regime e o ambiente da empresa."
      />
      <QueryState
        isPending={settings.isPending}
        isError={settings.isError}
        error={settings.error}
        onRetry={() => void settings.refetch()}
        what="a configuração da nota fiscal"
        isEmpty={false}
        empty={null}
      >
        {settings.data === undefined ? null : (
          <div className="flex flex-col gap-6">
            <Readiness settings={settings.data} />
            <Certificate settings={settings.data} />
            <Regime
              key={settings.data.regime?.validFrom ?? 'sem-regime'}
              settings={settings.data}
            />
            <Environment
              key={settings.data.version ?? 'nova'}
              settings={settings.data}
            />
            <FiscalServiceItems />
            <FiscalTaxProfiles />
            {settings.data.catalogOutdated ? (
              <InlineAlert tone="warning">
                Saiu uma lista oficial de serviços nova. A que o Aeris usa ainda
                é a anterior.
              </InlineAlert>
            ) : null}
          </div>
        )}
      </QueryState>
    </Page>
  )
}

const GapList = ({
  title,
  gaps,
}: {
  title: string
  gaps: readonly FiscalGap[]
}): ReactNode => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center gap-2">
      <span className="font-medium">{title}</span>
      {gaps.length === 0 ? (
        <Badge tone="success">Pronta</Badge>
      ) : (
        <Badge tone="warning">Falta configurar</Badge>
      )}
    </div>
    {gaps.length === 0 ? null : (
      <ul className="list-disc pl-5 text-(length:--text-sm) text-(--text-secondary)">
        {gaps.map((gap) => (
          <li key={gap}>{GAP_LABELS[gap]}</li>
        ))}
      </ul>
    )}
  </div>
)

const Readiness = ({
  settings,
}: {
  settings: FiscalSettingsResponse
}): ReactNode => (
  <Section
    title="O que falta para emitir"
    description="O crachá diz se os dados estão aqui, e não que o governo já autorizou uma nota."
  >
    <div className="flex flex-col gap-4">
      <GapList title="Nota de serviço" gaps={settings.gaps.nfse} />
      <GapList title="Nota de produto" gaps={settings.gaps.nfe} />
    </div>
  </Section>
)

const Certificate = ({
  settings,
}: {
  settings: FiscalSettingsResponse
}): ReactNode => {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [password, setPassword] = useState('')
  const [fileInput, setFileInput] = useState(0)
  const [confirming, setConfirming] = useState(false)

  const upload = useMutation({
    meta: { errorShownInline: true },
    mutationFn: async () => {
      if (file === null) throw new Error('Escolha o arquivo.')
      return await uploadFiscalCertificate({
        pfxBase64: await readBase64(file),
        certificatePassword: password,
      })
    },
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next)
      setFile(null)
      setPassword('')
      setFileInput((current) => current + 1)
    },
  })

  const remove = useMutation({
    meta: { errorShownInline: true },
    mutationFn: removeFiscalCertificate,
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next)
      setConfirming(false)
    },
  })

  const current = settings.certificate
  const failure = upload.isError
    ? message(upload.error, 'Não foi possível enviar o certificado agora.')
    : remove.isError
      ? message(remove.error, 'Não foi possível remover o certificado agora.')
      : null

  return (
    <Section
      title="Certificado A1"
      description="O arquivo .pfx da empresa. Ele fica cifrado e nunca aparece de volta na tela."
    >
      <div className="flex flex-col gap-3">
        {current === null ? null : (
          <div className="flex flex-wrap items-center gap-3">
            {current.daysLeft < 0 ? (
              <Badge tone="danger">Vencido</Badge>
            ) : current.daysLeft <= 30 ? (
              <Badge tone="warning">Vence em {current.daysLeft} dias</Badge>
            ) : (
              <Badge tone="success">Válido</Badge>
            )}
            <span className="text-(length:--text-sm)">
              {current.holderName.split(':')[0]}, até {dateOf(current.validTo)}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setConfirming(true)
              }}
            >
              Remover
            </Button>
          </div>
        )}
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            upload.mutate()
          }}
        >
          <Field
            label={current === null ? 'Arquivo .pfx' : 'Trocar por outro .pfx'}
            className="min-w-56 flex-1"
          >
            {(field) => (
              <Input
                {...field}
                key={fileInput}
                type="file"
                accept=".pfx,.p12,application/x-pkcs12"
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null)
                }}
              />
            )}
          </Field>
          <Field label="Senha do certificado" className="min-w-56 flex-1">
            {(field) => (
              <Input
                {...field}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value)
                }}
              />
            )}
          </Field>
          <Button
            type="submit"
            variant="primary"
            loading={upload.isPending}
            disabled={file === null || password === ''}
          >
            Conferir e salvar
          </Button>
        </form>
        {failure === null ? null : (
          <InlineAlert tone="danger">{failure}</InlineAlert>
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Remover o certificado?"
        description="A empresa deixa de emitir nota até enviar outro."
        confirmLabel="Remover"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate()
        }}
      />
    </Section>
  )
}

const Regime = ({
  settings,
}: {
  settings: FiscalSettingsResponse
}): ReactNode => {
  const queryClient = useQueryClient()
  const [crt, setCrt] = useState<FiscalRegime>(settings.regime?.crt ?? '1')
  const [validFrom, setValidFrom] = useState(settings.regime?.validFrom ?? '')

  const save = useMutation({
    meta: { errorShownInline: true },
    mutationFn: () => saveFiscalRegime({ crt, validFrom }),
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next)
    },
  })

  return (
    <Section
      title="Regime tributário"
      description="Confirme com o contador. Uma nota já emitida guarda o regime que valia no dia dela."
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate()
        }}
      >
        <Field label="Regime" className="min-w-56 flex-1">
          {(field) => (
            <Select
              {...field}
              value={crt}
              onChange={(event) => {
                setCrt(event.target.value as FiscalRegime)
              }}
            >
              {(Object.keys(REGIME_LABELS) as FiscalRegime[]).map((value) => (
                <option key={value} value={value}>
                  {REGIME_LABELS[value]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Vale a partir de" className="min-w-40">
          {(field) => (
            <Input
              {...field}
              type="date"
              value={validFrom}
              onChange={(event) => {
                setValidFrom(event.target.value)
              }}
            />
          )}
        </Field>
        <Button
          type="submit"
          variant="primary"
          loading={save.isPending}
          disabled={validFrom === ''}
        >
          Salvar regime
        </Button>
      </form>
      {save.isError ? (
        <InlineAlert tone="danger">
          {message(save.error, 'Não foi possível salvar o regime agora.')}
        </InlineAlert>
      ) : null}
    </Section>
  )
}

const Environment = ({
  settings,
}: {
  settings: FiscalSettingsResponse
}): ReactNode => {
  const queryClient = useQueryClient()
  const [environment, setEnvironment] = useState<FiscalEnvironment>(
    settings.environment
  )
  const [nfeSeries, setNfeSeries] = useState(String(settings.nfeSeries))
  const [nfseSeries, setNfseSeries] = useState(String(settings.nfseSeries))
  const [simplesPercent, setSimplesPercent] = useState(
    settings.simplesPercent ?? ''
  )
  const [informIm, setInformIm] = useState(settings.informMunicipalRegistration)
  const [nextNfe, setNextNfe] = useState(settings.nextNfeNumber)
  const [nextNfse, setNextNfse] = useState(settings.nextNfseNumber)

  const [confirming, setConfirming] = useState(false)

  const leavingForProduction =
    environment === 'producao' && settings.environment !== 'producao'

  const save = useMutation({
    meta: { errorShownInline: true },
    mutationFn: () =>
      saveFiscalSettings({
        environment,
        nfeSeries: Number(nfeSeries),
        nfseSeries: Number(nfseSeries),
        simplesPercent:
          simplesPercent.trim() === ''
            ? null
            : simplesPercent.trim().replace(',', '.'),
        informMunicipalRegistration: informIm,
        // Só manda o que mudou: o número só pode andar para a frente.
        ...(nextNfe === settings.nextNfeNumber
          ? {}
          : { nextNfeNumber: nextNfe }),
        ...(nextNfse === settings.nextNfseNumber
          ? {}
          : { nextNfseNumber: nextNfse }),

        version: settings.version,
        ...(leavingForProduction ? { confirmProduction: true } : {}),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next)
      setConfirming(false)
    },
    onError: () => {
      setConfirming(false)
    },
  })

  return (
    <Section
      title="Ambiente e séries"
      description="Em homologação a nota é um teste, sem valor fiscal. Em produção ela vale de verdade."
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (leavingForProduction) setConfirming(true)
          else save.mutate()
        }}
      >
        <Field label="Ambiente" className="min-w-56 flex-1">
          {(field) => (
            <Select
              {...field}
              value={environment}
              onChange={(event) => {
                setEnvironment(event.target.value as FiscalEnvironment)
              }}
            >
              <option value="homologacao">Homologação (teste)</option>
              <option value="producao">Produção (vale de verdade)</option>
            </Select>
          )}
        </Field>
        <Field label="Série da NF-e" className="w-32">
          {(field) => (
            <Input
              {...field}
              inputMode="numeric"
              value={nfeSeries}
              onChange={(event) => {
                setNfeSeries(event.target.value.replace(/\D/gu, ''))
              }}
            />
          )}
        </Field>
        <Field label="Série da NFS-e" className="w-32">
          {(field) => (
            <Input
              {...field}
              inputMode="numeric"
              value={nfseSeries}
              onChange={(event) => {
                setNfseSeries(event.target.value.replace(/\D/gu, ''))
              }}
            />
          )}
        </Field>
        <div className="flex w-full flex-col gap-3">
          <div className="flex flex-wrap gap-3">
            <Field
              label="Próximo número da NF-e"
              hint="Quem vem de outro sistema continua de onde parou."
              className="max-w-72 flex-1"
            >
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={nextNfe}
                  onChange={(event) => {
                    setNextNfe(event.target.value.replace(/\D/gu, ''))
                  }}
                />
              )}
            </Field>
            <Field
              label="Próximo número da NFS-e"
              hint="O número só pode andar para a frente."
              className="max-w-72 flex-1"
            >
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={nextNfse}
                  onChange={(event) => {
                    setNextNfse(event.target.value.replace(/\D/gu, ''))
                  }}
                />
              )}
            </Field>
          </div>

          <Field
            label="Percentual do Simples"
            hint="O contador informa. Sai em cada nota de serviço."
            className="max-w-72"
          >
            {(field) => (
              <Input
                {...field}
                inputMode="decimal"
                placeholder="6,00"
                value={simplesPercent}
                onChange={(event) => {
                  setSimplesPercent(event.target.value)
                }}
              />
            )}
          </Field>
          <CheckboxField
            label="Informar a Inscrição Municipal na nota de serviço (só se o contador confirmar que ela está no cadastro nacional)"
            checked={informIm}
            onCheckedChange={(checked) => {
              setInformIm(checked === true)
            }}
          />
        </div>
        <Button
          type="submit"
          variant="primary"
          loading={save.isPending}
          disabled={nfeSeries === '' || nfseSeries === ''}
        >
          Salvar
        </Button>
      </form>
      {save.isError ? (
        <InlineAlert tone="danger">
          {message(save.error, 'Não foi possível salvar agora.')}
        </InlineAlert>
      ) : null}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Passar para produção?"
        description="Toda nota emitida depois disso tem valor fiscal e vai para o governo de verdade."
        confirmLabel="Passar para produção"
        loading={save.isPending}
        onConfirm={() => {
          save.mutate()
        }}
      />
    </Section>
  )
}
